import ENVIRONMENT from "../config/environment";
import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { getSubscription } from "../_redux/subscription/subscriptionService";
// import { getSubscription } from '../redux/slices/subscriptionSlice';

export const useSocket = (user, token) => {
  const dispatch = useDispatch();
  const [socket, setSocket] = useState(null);
  const companyIdRef = useRef(user?.companyId?._id);
  const notificationDebounceRef = useRef(null);
  // ─── Socket creation ───
  useEffect(() => {
    if (user && !socket && token) {
      const chat = io(ENVIRONMENT.serverUrl, {
        query: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
      setSocket(chat);

      return () => {
        chat.disconnect();
        setSocket(null);
      };
    }
    //eslint-disable-next-line
  }, [token, user?.id]);

  // ─── Payment socket listeners ───
  useEffect(() => {
    if (!socket) return;

    const joinCompanyRoom = () => {
      const companyId = companyIdRef.current;
      if (companyId) {
        socket.emit("join_company", companyId);
        console.log(`📦 Joined company room: ${companyId}`);
      }
    };

    socket.on("connect", joinCompanyRoom);

    if (socket.connected) {
      joinCompanyRoom();
    }

    // ─── PAYMENT STATUS ───
    socket.on("payment_status", (data) => {
      console.log("Payment status received:", data);

      const moyasarResponse = data.moyasarResponse || data;
      const lastFour = moyasarResponse.source?.number?.slice(-4) || "****";

      // Forward to custom event
      try {
        window.dispatchEvent(new CustomEvent("payment-status", { detail: data }));
      } catch (_) {}

      // Handle FAILED payment
      if (moyasarResponse.status === "failed" || data.event === "PAYMENT_FAILED" || data.event === "payment_failed") {
        let errorMsg = moyasarResponse.source?.message || "Payment failed";

        //   if (data.i18nKey) {
        //     const translated = i18n(`Subscriptions.${data.i18nKey}`);
        //     if (translated && translated !== `Subscriptions.${data.i18nKey}`) {
        //       errorMsg = translated;
        //     }
        //   }

        toast.error(`${errorMsg} for card ending in ${lastFour}`);
        dispatch(getSubscription());
      }
      // Handle SUCCESSFUL payment
      else if (moyasarResponse.status === "paid" || data.event === "PAYMENT_PAID" || data.event === "payment_paid") {
        const amount = (moyasarResponse.amount / 100).toFixed(2);
        toast.success(`${amount} SAR paid successfully`);
        dispatch(getSubscription());
      }
    });

    // ─── PAYMENT CARD UPDATE ───
    socket.on("payment_card_update", async (data) => {
      console.log("Card update received:", data);

      if (data.type === "personal_card_update") return;

      const isReplacement = data.type === "card_replaced";
      const needsRetryConfirmation = data.needsRetryConfirmation === true;

      if (needsRetryConfirmation) {
        if (sessionStorage.getItem("cardAddToastShown") !== "1") {
          sessionStorage.setItem("cardAddToastShown", "1");
          setTimeout(() => sessionStorage.removeItem("cardAddToastShown"), 30 * 1000);
          toast.success(`Card added — confirm to retry your payment`);
        }
      } else if (isReplacement) {
        toast.success(`Card Updated Successfully`);
      } else {
        if (sessionStorage.getItem("cardAddToastShown") !== "1") {
          sessionStorage.setItem("cardAddToastShown", "1");
          setTimeout(() => sessionStorage.removeItem("cardAddToastShown"), 30 * 1000);
          toast.success(`Card Added Successfully`);
        }
      }

      dispatch(getSubscription());

      const intent = sessionStorage.getItem("subscriptionIntent");
      if (intent && !isReplacement) {
        window.dispatchEvent(new CustomEvent("cardAddedForSubscription"));
      } else if (needsRetryConfirmation && !isReplacement) {
        window.dispatchEvent(new CustomEvent("paymentRetryAfterCardAdd"));
      }
    });

    // ─── PAYMENT CARD ERROR ───
    socket.on("payment_card_error", (data) => {
      console.log("Card error received:", data);

      const moyasarResponse = data?.moyasarResponse || data || {};
      const lastFour = moyasarResponse.source?.number?.slice(-4) || "****";
      const errorMsg = moyasarResponse.source?.message || moyasarResponse.message || "Card verification failed";

      toast.error(`${errorMsg} for card ending in ${lastFour}`);
      dispatch(getSubscription());
    });

    // ─── SUBSCRIPTION UPDATED ───
    socket.on("subscription_updated", (data) => {
      console.log("Subscription updated:", data);
      dispatch(getSubscription());
    });

    // ─── Cleanup ───
    return () => {
      socket.off("connect", joinCompanyRoom);
      socket.off("payment_status");
      socket.off("payment_card_update");
      socket.off("payment_card_error");
      socket.off("subscription_updated");
    };
  }, [socket, dispatch]);

  return socket;
};
